import { ReactNode } from "react";
import { Link } from "@/lib/router-compat";
import { ArrowLeft, Wrench, ArrowRight, type LucideIcon } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export interface TrustBadge {
  icon: LucideIcon;
  label: string;
}

export interface FeatureCard {
  icon: LucideIcon;
  title: string;
  description: string;
}

export interface FAQ {
  q: string;
  a: string;
}

interface ToolPageShellProps {
  title: string;
  subtitle: string;
  badges: TrustBadge[];
  children: ReactNode;
  featuresHeading: string;
  features: FeatureCard[];
  faqHeading?: string;
  faqSubheading?: string;
  faqs: FAQ[];
  softCtaText: string;
}

const ToolPageShell = ({
  title,
  subtitle,
  badges,
  children,
  featuresHeading,
  features,
  faqHeading = "Frequently Asked Questions",
  faqSubheading,
  faqs,
  softCtaText,
}: ToolPageShellProps) => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Hero + Tool */}
        <section className="py-12 md:py-20">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto mb-10">
              <div className="flex justify-center mb-6">
                <Link
                  to="/tools"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-border bg-card text-sm font-medium text-foreground hover:bg-muted transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Free Tools
                </Link>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 text-center">
                {title}
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto text-center mb-6">
                {subtitle}
              </p>
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
                {badges.map((b) => (
                  <span key={b.label} className="inline-flex items-center gap-2">
                    <b.icon className="h-4 w-4" /> {b.label}
                  </span>
                ))}
              </div>
            </div>

            <div className="max-w-4xl mx-auto">{children}</div>
          </div>
        </section>

        {/* Features / What it does */}
        <section className="py-16 border-t border-border">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-10 text-center">
                {featuresHeading}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {features.map((f) => (
                  <div key={f.title} className="p-6 rounded-xl border border-border bg-card">
                    <f.icon className="h-6 w-6 mb-3 text-foreground" />
                    <h3 className="font-semibold mb-2">{f.title}</h3>
                    <p className="text-sm text-muted-foreground">{f.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
                {faqHeading}
              </h2>
              {faqSubheading && (
                <p className="text-muted-foreground text-center mb-10">{faqSubheading}</p>
              )}
              <Accordion type="single" collapsible className="w-full space-y-3">
                {faqs.map((item, i) => (
                  <AccordionItem
                    key={i}
                    value={`item-${i}`}
                    className="bg-secondary/50 border border-border ring-1 ring-white/[0.08] dark:ring-white/[0.12] rounded-lg px-6 data-[state=open]:bg-secondary/70"
                  >
                    <AccordionTrigger className="text-left text-base md:text-lg font-medium hover:no-underline py-5">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-5">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Back to tools */}
        <section className="py-16 border-t border-border">
          <div className="container px-4 sm:px-6 lg:px-8 text-center">
            <Link
              to="/tools"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border bg-card text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              <Wrench className="h-4 w-4" />
              Explore all free NETIUS tools
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </section>

        {/* Soft CTA */}
        <section className="py-12 border-t border-border">
          <div className="container px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-muted-foreground">
              {softCtaText}{" "}
              <Link to="/contact" className="text-foreground font-medium underline-offset-4 hover:underline">
                Talk to NETIUS →
              </Link>
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ToolPageShell;